/**
 * PRP-WEB-2: the plan page's edit state (moved out of V2PlanPage and grown). Loads the rooms
 * from the notes and the groups + underlay from `GET /plan/extras`; every change is one undo
 * step (`EditHistory`: plan and groups together); Save sends one `PUT /plan` with the changed and
 * new rooms, the deleted ones, and the groups/underlay when they changed. Nothing saves on its own.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { findRoom, type Plan } from '@sketch-core';
import { BackendAPI } from '../../../../lib/api';
import {
  ROOM_HAS_LINES, PlanSaveError, isPlanChanged, planApi, type PlanSaveExtras, type PlanSaveResult, type PlanUnderlay,
} from '../../../../lib/planApi';
import {
  afterSave, deletedRoomIds, loadEditPlan, runEdit, savePayload, setWallLength, type EditResult, type LoadedEditPlan, type RoomEditability,
} from '../planEdit';
import { EditHistory } from '../controller/editHistory';
import type { Sel } from '../controller/editController';
import { pruneGroups, sameGroups, type GroupResult, type Groups } from '../groups/groups';

export type Status = 'loading' | 'ready' | 'empty' | 'error';
type Act = (p: Plan) => { ok: boolean; message?: string };

/** A room made on the web since the load: editable, saved with `create: true`. */
export const NEW_ROOM: RoomEditability = { editable: true, baseVersion: 0, baseUpdatedAt: null, stored: {} };

/** Replace a plan's content with `w` (commits a finished drag through `runEdit`'s checks). */
const becomes = (w: Plan): Act => (p) => {
  p.rooms = w.rooms;
  p.defaults = w.defaults;
  p.next = w.next;
  return { ok: true };
};

export function useEditPlan(id: string) {
  const [status, setStatus] = useState<Status>('loading');
  const [loaded, setLoaded] = useState<LoadedEditPlan | null>(null);
  const [plan, setPlanState] = useState<Plan | null>(null);
  const planRef = useRef<Plan | null>(null);
  const history = useRef(new EditHistory());
  const [, setTick] = useState(0);
  const [sel, setSel] = useState<Sel | null>(null);
  const [groups, setGroupsState] = useState<Groups>([]);
  const [baseGroups, setBaseGroups] = useState<Groups>([]);
  const [projectToken, setProjectToken] = useState<string | null>(null);
  const [savedUnderlay, setSavedUnderlay] = useState<PlanUnderlay | null>(null);
  const [extrasUnderlay, setExtrasUnderlay] = useState<{ url: string; u: PlanUnderlay } | null>(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<PlanSaveResult | null>(null);
  const [conflict, setConflict] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const setPlan = (p: Plan | null) => { planRef.current = p; setPlanState(p); };
  const bump = () => setTick((t) => t + 1);

  const load = useCallback(async () => {
    const [notes, extras] = await Promise.all([
      BackendAPI.getNotesForProject(id),
      // Groups and the underlay are extras: the plan opens without them (an older server, a failed read).
      Promise.resolve().then(() => planApi.getExtras(id)).catch(() => ({ groups: [], underlay: null, project_updated_at: null })),
    ]);
    const L = loadEditPlan(Array.isArray(notes) ? notes : []);
    history.current = new EditHistory();
    const g = pruneGroups(extras.groups ?? [], L?.plan.rooms.map((r) => r.id) ?? []);
    history.current.groups = g;
    setGroupsState(g);
    setBaseGroups(g);
    setProjectToken(extras.project_updated_at ?? null);
    const xu = extras.underlay;
    const su = xu && xu.path ? { path: xu.path, scale_ft_per_px: xu.scale_ft_per_px, offset: xu.offset, rotation: xu.rotation ?? 0, opacity: xu.opacity } : null;
    setSavedUnderlay(su);
    setExtrasUnderlay(xu && su ? { url: xu.url, u: su } : null);
    setLoaded(L);
    setPlan(L?.plan ?? null);
    setConflict(false);
    setSaveError(null);
    if (!L || !L.plan.rooms.length) { setStatus('empty'); return L; }
    setSel((s) => (s && L.plan.rooms.some((r) => r.id === s.rid) ? s : null));
    setStatus('ready');
    return L;
  }, [id]);

  const editability = useCallback((rid: string): RoomEditability => loaded?.rooms[rid] ?? NEW_ROOM, [loaded]);
  /** Null when `rid` may be changed here, else why not (DD7). */
  const editable = useCallback((rid: string): string | null => {
    const e = editability(rid);
    return e.editable ? null : e.reason;
  }, [editability]);

  /** Run one core action as one undo step; the refusal message, or null. */
  const act = useCallback((action: Act, after?: { select?: Sel | null }): string | null => {
    const p = planRef.current;
    if (!p || !loaded) return 'The plan is not loaded.';
    history.current.groups = groups;
    const out = runEdit(p, history.current, loaded.rooms, action);
    if (!out.plan) {
      const m = (out.res as { message?: string }).message ?? 'That did not change anything.';
      setNote(m);
      return m;
    }
    setPlan(out.plan);
    setResult(null);
    if (after?.select !== undefined) setSel(after.select && findRoom(out.plan, after.select.rid) ? after.select : null);
    else setSel((s) => (s && findRoom(out.plan!, s.rid) ? s : null));
    bump();
    return null;
  }, [loaded, groups]);

  /** Commit a finished drag (`before` -> `working`) as one undo step. */
  const commit = useCallback((before: Plan, working: Plan, nextSel: Sel | null): string | null => {
    if (!loaded) return 'The plan is not loaded.';
    history.current.groups = groups;
    const out = runEdit(before, history.current, loaded.rooms, becomes(working));
    if (!out.plan) {
      setPlan(before);
      const m = (out.res as { message?: string }).message ?? 'That did not change anything.';
      setNote(m);
      return m;
    }
    setPlan(out.plan);
    setResult(null);
    setSel(nextSel);
    bump();
    return null;
  }, [loaded, groups]);

  /** A grouping change, one undo step (the plan itself does not change). */
  const changeGroups = useCallback((fn: (g: Groups) => GroupResult): string | null => {
    const p = planRef.current;
    if (!p) return 'The plan is not loaded.';
    const res = fn(groups);
    if (!res.ok) { setNote(res.message); return res.message; }
    history.current.groups = groups;
    history.current.checkpoint(p);
    history.current.groups = res.groups;
    setGroupsState(res.groups);
    setResult(null);
    bump();
    return null;
  }, [groups]);

  const wallLength = useCallback((rid: string, vid: number, ft: number, opts?: { moveEnd?: boolean; chosen?: boolean }): EditResult => {
    const p = planRef.current;
    if (!p || !loaded) return { ok: false, message: 'The plan is not loaded.' };
    history.current.groups = groups;
    const out = setWallLength(p, history.current, loaded.rooms, rid, vid, ft, opts);
    if (out.plan) { setPlan(out.plan); setResult(null); bump(); }
    return out.result;
  }, [loaded, groups]);

  const step = (dir: 'undo' | 'redo') => {
    const p = planRef.current;
    if (!p) return;
    history.current.groups = groups;
    const q = dir === 'undo' ? history.current.undo(p) : history.current.redo(p);
    if (!q) return;
    setPlan(q);
    setGroupsState(history.current.groups);
    setSel((s) => (s && findRoom(q, s.rid) ? s : null));
    bump();
  };

  /** Only for previews (a drag in progress, or Esc putting the plan back): no history. */
  const preview = (p: Plan) => setPlan(p);

  const roomIds = useMemo(() => plan?.rooms.map((r) => r.id) ?? [], [plan]);
  const liveGroups = useMemo(() => pruneGroups(groups, roomIds), [groups, roomIds]);
  const pending = useMemo(() => (loaded && plan ? savePayload(loaded.plan, plan, loaded.rooms) : []), [loaded, plan]);
  const deleted = useMemo(() => (loaded && plan ? deletedRoomIds(loaded.plan, plan, loaded.rooms) : []), [loaded, plan]);
  const groupsDirty = !sameGroups(liveGroups, baseGroups);

  const save = async (underlay: PlanUnderlay | null, underlayDirty: boolean) => {
    if (saving || !loaded || !plan) return;
    if (!pending.length && !deleted.length && !groupsDirty && !underlayDirty) return;
    setSaving(true);
    setSaveError(null);
    setConflict(false);
    const extras: PlanSaveExtras = {};
    if (groupsDirty) extras.groups = liveGroups;
    if (deleted.length) extras.deleted_room_ids = deleted;
    if (underlayDirty) extras.underlay = underlay;
    if (groupsDirty || underlayDirty) extras.base_project_updated_at = projectToken;
    try {
      const res = await planApi.savePlan(id, pending, extras);
      setResult(res);
      // What was sent is what is stored now: it becomes the base. History restarts from here.
      history.current = new EditHistory();
      history.current.groups = liveGroups;
      setGroupsState(liveGroups);
      setBaseGroups(liveGroups);
      if (underlayDirty) setSavedUnderlay(underlay);
      const tok = (res as { project_updated_at?: string | null }).project_updated_at;
      if (tok !== undefined) setProjectToken(tok);
      const rooms = afterSave(loaded.rooms, pending, res.rooms ?? []);
      for (const d of deleted) delete rooms[d];
      setLoaded({ ...loaded, plan, rooms });
      bump();
    } catch (err) {
      if (isPlanChanged(err) || (err as { code?: string })?.code === 'plan_changed') setConflict(true);
      else if (err instanceof PlanSaveError) {
        setSaveError(err.message);
        // room_has_lines: show the room it names; the other edits stay unsaved, not dropped.
        if (err.code === ROOM_HAS_LINES && err.roomId) {
          const r = loaded.plan.rooms.find((q) => q.id === err.roomId);
          if (r && !plan.rooms.some((q) => q.id === r.id)) setSaveError(`${err.message} Undo the delete of ${r.name} to save the rest.`);
          else if (r) setSel({ type: 'room', rid: r.id });
        } else if (err.roomId && findRoom(plan, err.roomId)) setSel({ type: 'room', rid: err.roomId });
      } else setSaveError((err as Error)?.message || 'Could not save the plan. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return {
    status, setStatus, load, loaded, plan, planRef, preview, history: history.current, sel, setSel,
    groups: liveGroups, act, commit, changeGroups, wallLength, undo: () => step('undo'), redo: () => step('redo'),
    editable, editability, pending, deleted, groupsDirty, save, saving, result, setResult, conflict, saveError,
    note, setNote, savedUnderlay, extrasUnderlay,
  };
}

export type EditPlan = ReturnType<typeof useEditPlan>;
